import { useDrag } from '@use-gesture/react';
import { RefCallback, useCallback, useEffect, useRef } from 'react';

import { usePanelsContext } from './_usePanelsContext.js';

export interface UseResizableReturnType {
  /** Ref for the custom resizer element */
  resizerRef: RefCallback<HTMLDivElement>;
}

export type UseResizableProps = {
  /**
   * The order of the resize handle.
   * Used to map the handle to the corresponding resizable panels.
   */
  order: number;
};

/** @internal */
export const useResizable = ({
  order,
}: UseResizableProps): UseResizableReturnType => {
  const { generateResizeEventHandlers } = usePanelsContext();

  if (!generateResizeEventHandlers) {
    throw new Error(
      'The useResizable hook must be used within a PanelsProvider.',
    );
  }

  const resizerRef = useRef<HTMLDivElement | null>(null);
  const resizerCallbackRef = useCallback((node: HTMLDivElement) => {
    resizerRef.current = node;
  }, []);
  const { onDragStart, onDrag, onDragEnd, onKeyDown } =
    generateResizeEventHandlers(order);

  useDrag(
    (dragEvent) => {
      if (dragEvent.type === 'pointerdown') {
        onDragStart();
        return;
      }

      if (dragEvent.type === 'pointermove') {
        const [draggedAmount] = dragEvent.delta;
        const direction = Math.sign(draggedAmount);
        const [mousePosition] = dragEvent.xy;
        const resizeHandlePosition =
          resizerRef.current?.getBoundingClientRect().left || 0;

        // If the mouse is on the wrong side of the resize handle when dragging,
        // don't do anything just yet. This ensures that the cursor is always
        // close to the resize handle when actually resizing.
        if (
          (direction < 0 && mousePosition > resizeHandlePosition) ||
          (direction > 0 && mousePosition < resizeHandlePosition)
        ) {
          return;
        }

        onDrag(draggedAmount);
        return;
      }

      // The onDragEnd should be called anytime we stop the dragging action independent of the reason
      if (
        dragEvent.type === 'pointerup' ||
        dragEvent.type === 'lostpointercapture'
      ) {
        onDragEnd();
        resizerRef.current?.blur();
        return;
      }
    },
    {
      target: resizerRef,
      eventOptions: { passive: false },
      drag: { filterTaps: true },
    },
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        onKeyDown(-10);
      }

      if (e.key === 'ArrowRight') {
        onKeyDown(10);
      }
    };

    resizerRef.current?.addEventListener('keydown', handleKeyDown);

    return () => {
      resizerRef.current?.removeEventListener('keydown', handleKeyDown);
    };
  }, [onKeyDown, resizerRef]);

  return {
    resizerRef: resizerCallbackRef,
  };
};
